import React, { createContext, useContext, useState, useEffect } from 'react';

export interface CartItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  options: Record<string, string | number>;
  estimatedPrice: number; // Subtotal for this item (price * quantity)
  designFileName: string | null;
  uploadedFileUrl: string | null;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: Omit<CartItem, 'id'>) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  cartCount: number;
  cartTotal: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Failed to parse cart from localStorage:', e);
      }
    }
  }, []);

  // Save cart to localStorage on change
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (newItem: Omit<CartItem, 'id'>) => {
    setCartItems((prevItems) => {
      // Check if item with same product and exact same specifications already exists
      const existingItemIndex = prevItems.findIndex(
        (item) =>
          item.productId === newItem.productId &&
          JSON.stringify(item.options) === JSON.stringify(newItem.options) &&
          item.designFileName === newItem.designFileName
      );

      if (existingItemIndex > -1) {
        // If it exists, update quantity and subtotal price
        const updatedItems = [...prevItems];
        const existingItem = updatedItems[existingItemIndex];
        const newQty = existingItem.quantity + newItem.quantity;
        
        // Recalculate price: base price per unit * new quantity
        const basePrice = existingItem.estimatedPrice / existingItem.quantity;
        existingItem.quantity = newQty;
        existingItem.estimatedPrice = Math.round(basePrice * newQty);
        return updatedItems;
      } else {
        // If it's new, generate a unique local ID and append
        const id = `${Date.now()}-${newItem.productId}-${Math.random().toString(36).substring(2, 7)}`;
        return [...prevItems, { ...newItem, id }];
      }
    });
  };

  const removeFromCart = (id: string) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== id));
  };

  const updateQuantity = (id: string, newQty: number) => {
    if (newQty < 1) return;
    setCartItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === id) {
          const basePrice = item.estimatedPrice / item.quantity;
          return {
            ...item,
            quantity: newQty,
            estimatedPrice: Math.round(basePrice * newQty),
          };
        }
        return item;
      })
    );
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('cart');
  };

  // Sum of unique items count or total quantity
  const cartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);
  const cartTotal = cartItems.reduce((acc, item) => acc + item.estimatedPrice, 0);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartCount,
        cartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
